/**
 * Growth, the first-week loop: the north star per signup week and the first
 * read of the outcome log, from GET /provider/admin/growth/first-week (pv-auth
 * services/growth/firstWeek.service.ts and outcome.pure.ts). Types mirror the
 * payload; the helpers decide how a number is printed, and the rule is the
 * server's: a rate is printed as a percent only when the server gave one
 * (MIN_GROUP closed windows); otherwise the counts and the words, never a 0.
 */
import { NOT_MEASURED } from './format';

export interface Rate {
  closed: number;
  reached: number;
  pending: number;
  share: number | null;
  reason: string | null;
}

export interface FirstWeekRow {
  weekStart: string;
  current: boolean;
  signups: number;
  firstChecks: number;
  secondCheckWithin48h: Rate;
  threeChecksInFirst7Days: Rate;
}

export interface OutcomeKindRead {
  count: number;
  rate: Rate;
}

export interface OutcomesRead {
  days: number;
  since: string;
  reminders: OutcomeKindRead & { byChannel: Record<string, number>; windowHours: number };
  activationsOffered: OutcomeKindRead;
  routinesStarted: OutcomeKindRead;
  lastResolvedAt: string | null;
}

export interface FirstWeekPayload {
  generatedAt: string;
  fetchedAt: string;
  cached: boolean;
  minGroup: number;
  definitions: { weeks: string; secondCheck: string; firstWeek: string; sitting: string; reminder: string; activation: string };
  current: FirstWeekRow;
  weeks: FirstWeekRow[];
  outcomes: OutcomesRead | null;
  excluded: { team: number; qa: number };
  unavailable: { source: string; reason: string }[];
}

export async function fetchFirstWeek(): Promise<FirstWeekPayload> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/growth/first-week', true)) as FirstWeekPayload;
}

/* ── Pure helpers ───────────────────────────────────────────────────────── */

export const NOT_ENOUGH = 'not enough data';

/** A share 0 to 1 as a whole percent, or null when the server gave none. */
export const pctOf = (r: Rate): number | null => (typeof r.share === 'number' && Number.isFinite(r.share) ? Math.round(r.share * 100) : null);

/**
 * The headline of a rate: "70%" when the server printed one, else the words
 * ("not enough data" under the floor, or the server's reason when nothing has
 * closed). Never a 0 for a rate nobody could read.
 */
export const rateHead = (r: Rate): string => {
  const p = pctOf(r);
  if (p !== null) return `${p}%`;
  if (r.closed > 0) return NOT_ENOUGH;
  return r.reason ?? NOT_MEASURED;
};

/** The sample line under a rate: "7 of 10 closed", plus "3 still open" when some windows are running. */
export const rateSample = (r: Rate): string => {
  const parts: string[] = [];
  if (r.closed > 0) parts.push(`${r.reached} of ${r.closed} closed`);
  if (r.pending > 0) parts.push(`${r.pending} still open`);
  return parts.join(' · ');
};

/** "Oct 19" for a YYYY-MM-DD week start, read as a calendar date (no zone shift). */
export const weekLabel = (weekStart: string): string => {
  const [y, m, d] = weekStart.split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12));
  return dt.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** "email 4, push 9" for the channels the reminders went out on, or "none". */
export const channelLine = (byChannel: Record<string, number>): string => {
  const keys = Object.keys(byChannel).sort();
  return keys.length ? keys.map((k) => `${k} ${byChannel[k]}`).join(', ') : 'none';
};

/** The newest whole week whose second-check rate has a percent, for the section summary; null when none has. */
export const latestReadable = (weeks: FirstWeekRow[]): FirstWeekRow | null =>
  weeks.find((w) => pctOf(w.secondCheckWithin48h) !== null) ?? null;
